import React, { FC, Fragment, useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";

import { actions } from "../../../dispatch";
import { dispatch } from "../../../dispatch/dispatch";
import ContextStore from "../../../hooks/ContextStore";
import { FromToDateSelect } from "../../../interfaces/formData/FromToDateSelect.interface";
import { IOverviewTableValues } from "../../../interfaces/formData/OverViewTableValues.interface";
import { IErrorResponse } from "../../../interfaces/responses/ErrorResponse.interface";
import { IProductGroupResponse } from "../../../interfaces/responses/ProductGroupResponse.interface";
import { IProductResponse } from "../../../interfaces/responses/ProductResponse.interface";
import { IStoreResponse } from "../../../interfaces/responses/StoreResponse.interface";
import { CreateGroupModal } from "../../../modals";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { fromDateToString, fromStringToDate } from "./utils";
import { IAnalyticsResponse } from "../../../interfaces/responses/AnalyticsResponse.interface";

// ── All business logic is 100% unchanged ──────────────────────────────────
export const Analytics: FC = () => {
    const { contextStore, setContextStore } = useContext(ContextStore);
    const { id } = useParams();
    const fixedDate: FromToDateSelect = {
        fromDate: new Date(2022, 5, 3),
        toDate: new Date(Date.now()),
    };
    const onChangeDate = (e: React.ChangeEvent<HTMLInputElement>) => {
        let vDate = contextStore.date;
        vDate = { ...vDate, [e.target.name]: fromStringToDate(e.target.value) };
        setContextStore((past: any) => {
            return { ...past, date: vDate };
        });
    };
    const [graphData, setGraphData] = useState<Array<IAnalyticsResponse>>([]);
    const [dateUpdate, setDateUpdate] = useState<number>(0);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [tableValues, setTableValues] = useState<IOverviewTableValues>({
        deliveredOrders: 0,
        canceledOrders: 0,
        returnedOrders: 0,
        deliveredPercentage: 0,
        totalPayout: 0,
        totalBuyingRate: 0,
        totalTransportFees: 0,
        totalStorageFees: 0,
        totalPackageFees: 0,
        totalProfit: 0,
        roi: 0,
        profitPerProduct: 0,
        failedOrders: 0,
        totalOrders: 0,
        totalLoss: 0,
        progressDeliveredOrders: 0,
        progressProfit: 0,
        pendingOrders: 0,
        shippedOrders: 0,
        readyToShipOrders: 0,
        progressTotalOrders: 0,
    });
    const tableData = [
        { name: "Delivered Orders", value: tableValues.deliveredOrders },
        { name: "Progress (Delivered)%", value: tableValues.progressDeliveredOrders },
        { name: "Canceled Orders", value: tableValues.canceledOrders },
        { name: "Returned Orders", value: tableValues.returnedOrders },
        { name: "Failed Orders", value: tableValues.failedOrders },
        { name: "Delivery Rate %", value: tableValues.deliveredPercentage.toFixed(0) },
        { name: "Shipped Orders", value: tableValues.shippedOrders },
        { name: "Pending Orders", value: tableValues.pendingOrders },
        { name: "Ready To Ship", value: tableValues.readyToShipOrders },
        { name: "Total Orders", value: tableValues.totalOrders },
        { name: "Progress (Total Orders)%", value: tableValues.progressTotalOrders },
        { name: "Total Payout", value: tableValues.totalPayout.toFixed(2) },
        { name: "Total Buying Cost", value: tableValues.totalBuyingRate.toFixed(2) },
        { name: "Transport Fees", value: tableValues.totalTransportFees.toFixed(2) },
        { name: "Storage Fees", value: tableValues.totalStorageFees.toFixed(2) },
        { name: "Package Fees", value: tableValues.totalPackageFees.toFixed(2) },
        { name: "Total Profit", value: tableValues.totalProfit.toFixed(2), highlight: true },
        { name: "Progress (Profit)%", value: tableValues.progressProfit },
        { name: "ROI %", value: tableValues.roi.toFixed(0) },
        { name: "Profit / Product", value: tableValues.profitPerProduct },
        { name: "Total Loss", value: tableValues.totalLoss },
    ];
    useEffect(() => {
        (async () => {
            if (id !== "0") {
                contextStore.setShowSpinner(true);
                const storeResponse: IStoreResponse | IErrorResponse =
                    await dispatch(actions.getStore, {}, contextStore.token, { id });
                if ("error" in storeResponse) {
                    contextStore.setShowSpinner(false);
                    return console.log(storeResponse.error.message);
                }
                const productsResponse: Array<IProductResponse> | IErrorResponse =
                    await dispatch(
                        actions.getStoreProducts,
                        {
                            fromDate: contextStore.date.fromDate.toISOString(),
                            toDate: contextStore.date.toDate.toISOString(),
                        },
                        contextStore.token,
                        { storeId: id }
                    );
                if ("error" in productsResponse) {
                    contextStore.setShowSpinner(false);
                    return console.log(productsResponse.error.message);
                }
                const productGroupsResponse: Array<IProductGroupResponse> | IErrorResponse =
                    await dispatch(
                        actions.getStoreProductGroups,
                        {},
                        contextStore.token,
                        { storeId: id }
                    );
                if ("error" in productGroupsResponse) {
                    contextStore.setShowSpinner(false);
                    return console.log(productGroupsResponse.error.message);
                }
                console.log({
                    fromDate: contextStore.date.fromDate.toISOString(),
                    toDate: contextStore.date.toDate.toISOString(),
                    storeId: id,
                });
                const analyticsResponse: Array<IAnalyticsResponse> | IErrorResponse =
                    await dispatch(
                        actions.getAnalyticsAll,
                        {
                            fromDate: contextStore.date.fromDate.toISOString(),
                            toDate: contextStore.date.toDate.toISOString(),
                            storeId: id,
                        },
                        contextStore.token
                    );
                if ("error" in analyticsResponse) {
                    contextStore.setShowSpinner(false);
                    return console.log(analyticsResponse.error.message);
                }
                setGraphData(
                    analyticsResponse.map((data) => {
                        data.totalProfit = parseFloat(data.totalProfit.toFixed(2));
                        return data;
                    })
                );
                setContextStore((past: any) => {
                    return {
                        ...past,
                        products: productsResponse,
                        store: storeResponse,
                        productGroups: productGroupsResponse,
                    };
                });
                contextStore.setShowSpinner(false);
            } else {
                setContextStore((past: any) => {
                    return {
                        ...past,
                        products: [],
                        store: {} as IStoreResponse,
                        productGroups: [],
                    };
                });
            }
        })();
    }, [id, contextStore.storeUpdate, dateUpdate]);

    const updateProducts = (products: Array<IProductResponse>) => {
        let vValues = {
            deliveredOrders: 0,
            canceledOrders: 0,
            returnedOrders: 0,
            deliveredPercentage: 0,
            totalPayout: 0,
            totalBuyingRate: 0,
            totalTransportFees: 0,
            totalStorageFees: 0,
            totalPackageFees: 0,
            totalProfit: 0,
            roi: 0,
            failedOrders: 0,
            totalOrders: 0,
            profitPerProduct: 0,
            totalLoss: 0,
            progressDeliveredOrders: 0,
            progressProfit: 0,
            pendingOrders: 0,
            shippedOrders: 0,
            readyToShipOrders: 0,
            progressTotalOrders: 0,
        } as IOverviewTableValues;
        let progressDeliveredOrdersBefore = 0;
        let progressProfitBefore = 0;
        let progressDeliveredOrdersAfter = 0;
        let progressProfitAfter = 0;
        let progressTotalOrdersBefore = 0;
        let progressTotalOrdersAfter = 0;
        products.map((product) => {
            vValues.deliveredOrders += product.deliveredOrders;
            vValues.canceledOrders += product.canceledOrders;
            vValues.returnedOrders += product.returnedOrders;
            vValues.pendingOrders += product.pendingOrders;
            vValues.shippedOrders += product.shippedOrders;
            vValues.readyToShipOrders += product.readyToShipOrders;
            vValues.deliveredPercentage += product.deliveredPercentage;
            vValues.totalPayout += product.totalPayout;
            vValues.totalBuyingRate += product.totalBuyingRate;
            vValues.totalTransportFees += product.totalTransportFees;
            vValues.totalStorageFees += product.totalStorageFees;
            vValues.totalPackageFees += product.totalPackageFees;
            vValues.totalProfit += product.totalProfit;
            vValues.roi += product.roi;
            vValues.failedOrders += product.failedOrders;
            vValues.totalOrders +=
                product.deliveredOrders +
                product.returnedOrders +
                product.canceledOrders +
                product.failedOrders +
                product.pendingOrders +
                product.shippedOrders +
                product.readyToShipOrders;
            vValues.profitPerProduct += product.profitPerProduct;
            vValues.totalLoss += product.loss;
            progressDeliveredOrdersBefore += product.progressDeliveredOrdersBefore;
            progressDeliveredOrdersAfter += product.progressDeliveredOrdersAfter;
            progressProfitBefore += product.progressProfitBefore;
            progressProfitAfter += product.progressProfitAfter;
            progressTotalOrdersAfter += product.progressTotalOrdersAfter;
            progressTotalOrdersBefore += product.progressTotalOrdersBefore;
            progressProfitAfter += product.progressProfitAfter;
            progressProfitBefore += product.progressProfitBefore;
        });
        vValues.deliveredPercentage = parseFloat(
            (
                (vValues.deliveredOrders /
                    (vValues.deliveredOrders + vValues.returnedOrders)) *
                100
            ).toFixed(2)
        );
        vValues.roi = parseFloat(
            ((vValues.totalProfit / vValues.totalBuyingRate) * 100).toFixed(2)
        );
        vValues.profitPerProduct = parseFloat(
            (vValues.totalProfit / vValues.deliveredOrders).toFixed(2)
        );
        if (progressDeliveredOrdersBefore != 0) {
            vValues.progressDeliveredOrders = parseFloat(
                (
                    ((progressDeliveredOrdersAfter - progressDeliveredOrdersBefore) /
                        progressDeliveredOrdersBefore) *
                    100
                ).toFixed(2)
            );
        }
        if (progressProfitBefore != 0) {
            vValues.progressProfit = parseFloat(
                (
                    ((progressProfitAfter - progressProfitBefore) /
                        progressProfitBefore) *
                    100
                ).toFixed(2)
            );
        }
        if (progressTotalOrdersBefore != 0) {
            vValues.progressTotalOrders = parseFloat(
                (
                    ((progressTotalOrdersAfter - progressTotalOrdersBefore) /
                        progressTotalOrdersBefore) *
                    100
                ).toFixed(2)
            );
        }
        setTableValues(vValues);
    };

    useEffect(() => {
        setContextStore((past: any) => {
            return { ...past, overviewTrigger: updateProducts };
        });
    }, []);

    useEffect(() => {
        updateProducts(contextStore.products);
        contextStore.productGroupTrigger(
            contextStore.productGroups,
            contextStore.products
        );
        contextStore.productTrigger(contextStore.products);
    }, [
        contextStore.productGroupTrigger,
        contextStore.products,
        contextStore.productGroups,
        contextStore.productTrigger,
    ]);

    const [legend, setLegend] = useState<string[]>([
        contextStore.user.type === "admin" ? "totalProfit" : "deliveredOrders",
    ]);
    const selectLegend = (value: string) => setLegend([...legend, value]);
    const deselectLegend = (value: string) =>
        setLegend(legend.filter((l) => l !== value));
    const handleLegend = (value: string) =>
        legend.some((l) => l === value) ? deselectLegend(value) : selectLegend(value);

    const legendItems = [
        ...(contextStore.user.type === "admin"
            ? [{ key: "totalProfit", label: "Total Profit", fill: "#00aeef" }]
            : []),
        { key: "deliveredOrders", label: "Delivered", fill: "#52b041" },
        { key: "returnedOrders", label: "Returned", fill: "#b04141" },
        { key: "failedOrders", label: "Failed", fill: "#b0aa41" },
        { key: "canceledOrders", label: "Canceled", fill: "#b07741" },
        { key: "totalOrders", label: "Total Orders", fill: "#b041b0" },
        { key: "shippedOrders", label: "Shipped", fill: "#6f2278" },
        { key: "readyToShipOrders", label: "RTS", fill: "#78223f" },
        { key: "pendingOrders", label: "Pending", fill: "#223a78" },
    ];

    // ── JSX redesigned with Tailwind — all logic above unchanged ─────────────
    return (
        <Fragment>
            {/* ── Section header ── */}
            <div className="flex items-center justify-between px-6 pt-6 pb-2">
                <h2 className="text-base font-bold text-foreground tracking-tight">Overview Analytics</h2>
            </div>

            {/* ── Top row: summary table + date controls ── */}
            <div className="mx-6 mb-4 grid grid-cols-1 gap-4 lg:grid-cols-[1fr_280px]">

                {/* Summary table — admin only */}
                {contextStore.user.type === "admin" && (
                    <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
                        <div className="overflow-y-auto max-h-64">
                            <table className="w-full text-sm border-collapse">
                                <thead>
                                    <tr>
                                        <th className="sticky top-0 bg-slate-800 dark:bg-slate-900 text-slate-100 text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider">
                                            Metric
                                        </th>
                                        <th className="sticky top-0 bg-slate-800 dark:bg-slate-900 text-slate-100 text-right px-4 py-2.5 text-xs font-semibold uppercase tracking-wider">
                                            Value
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tableData.map((row, i) => (
                                        <tr
                                            key={i}
                                            className={
                                                row.highlight
                                                    ? "bg-emerald-700 dark:bg-emerald-800"
                                                    : i % 2 === 0
                                                    ? "bg-card"
                                                    : "bg-muted/40"
                                            }
                                        >
                                            <td className={`px-4 py-1.5 text-xs ${row.highlight ? "text-white font-bold" : "text-muted-foreground"}`}>
                                                {row.name}
                                            </td>
                                            <td className={`px-4 py-1.5 text-xs text-right tabular-nums font-medium ${row.highlight ? "text-white font-bold text-sm" : "text-foreground"}`}>
                                                {row.value}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Date controls */}
                <div className="rounded-xl border border-border bg-card p-4 shadow-sm flex flex-col gap-3">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Date Range</h3>
                    <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">Start Date</label>
                        <input
                            type="date"
                            name="fromDate"
                            value={fromDateToString(contextStore.date.fromDate)}
                            min={fromDateToString(fixedDate.fromDate)}
                            max={fromDateToString(contextStore.date.toDate)}
                            onChange={onChangeDate}
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">End Date</label>
                        <input
                            type="date"
                            name="toDate"
                            value={fromDateToString(contextStore.date.toDate)}
                            min={fromDateToString(contextStore.date.fromDate)}
                            max={fromDateToString(fixedDate.toDate)}
                            onChange={onChangeDate}
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                    </div>
                    <button
                        onClick={() => setDateUpdate(dateUpdate + 1)}
                        className="w-full rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 transition-colors"
                    >
                        Apply Date Range
                    </button>
                </div>
            </div>

            {/* ── Chart area ── */}
            <div className="mx-6 mb-6 rounded-xl border border-border bg-card p-4 shadow-sm">
                {/* Legend pills */}
                <div className="mb-3 flex flex-wrap gap-2">
                    {legendItems.map(({ key, label, fill }) => {
                        const active = legend.some((l) => l === key);
                        return (
                            <button
                                key={key}
                                onClick={() => handleLegend(key)}
                                className={`rounded-full px-3 py-1 text-xs font-medium border transition-all ${
                                    active
                                        ? "border-transparent text-white"
                                        : "border-border bg-background text-muted-foreground hover:border-muted-foreground"
                                }`}
                                style={active ? { backgroundColor: fill, borderColor: fill } : {}}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>

                {/* Recharts area chart */}
                <ResponsiveContainer width="99%" aspect={5}>
                    <AreaChart data={graphData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <Tooltip />
                        {legendItems
                            .filter(({ key }) => legend.some((l) => l === key))
                            .map(({ key, fill }) => (
                                <Area
                                    key={key}
                                    type="monotone"
                                    dataKey={key}
                                    stroke={fill}
                                    fillOpacity={0.35}
                                    fill={fill}
                                />
                            ))}
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </Fragment>
    );
};
